import { logClientError } from 'src/util/server-log';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";
import { catchError, map, switchMap } from "rxjs/operators";
import { from, Observable, of } from "rxjs";
import { Injectable } from "@angular/core";
import { CognitoService } from "@services/cognito/cognito.service";
import { SharedService } from "@services/shared/shared.service";
import { LoadingService } from "@services/loading/loading.service";
import { User } from "@models/user";
import { CognitoUserSession } from "amazon-cognito-identity-js";
import { CookieService } from "ngx-cookie-service";
import { SystemService } from "@services/system/system.service";
import { GrpcAuthService } from "@services/grpc-auth/grpc-auth.service";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class AuthGuard {
  user?: User;

  constructor(
    private router: Router,
    private shared: SharedService,
    private loadingService: LoadingService,
    private cognitoService: CognitoService,
    private cookieService: CookieService,
    private system: SystemService,
    private grpcAuthService: GrpcAuthService,
  ) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.loadingService.loading$.next(true);
    return this.bootstrapAuthGuard(next, state);
  }

  private bootstrapAuthGuard(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this.system.AUTH_PROVIDER === "cognito") {
      return this.handleCognitoAuth(next, state);
    } else if (this.system.AUTH_PROVIDER === "grpc") {
      return this.handleGrpcAuth(next, state);
    } else {
      throw new Error("Invalid auth provider");
    }
  }

  private checkStoredUser() {
    if (!this.shared.accessToken) {
      this.shared.accessToken = this.cookieService.get("accessToken") || null;
    }
    if (!this.shared.refreshToken) {
      this.shared.refreshToken = this.cookieService.get("refreshToken") || null;
    }
    if (!this.shared.user) {
      const userJson = this.cookieService.get("user");
      if (userJson) {
        try {
          this.shared.user = JSON.parse(userJson);
        } catch (e) {
          logClientError("Error parsing user from cookie:", e);
        }
      }
    }
  }

  private handleGrpcAuth(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const code = next.queryParams["code"];
    // Try sessionStorage first, then localStorage as fallback
    let currentState = sessionStorage.getItem("oauth_state") || localStorage.getItem("oauth_state");
    if (code) {
      if (!currentState) {
        return this.handleLoginRedirect("Missing state in storage for OAuth callback");
      }
      return this.handleOAuthCallback(code, currentState);
    }
    this.checkStoredUser();
    if (this.shared.accessToken) {
      return from(this.grpcAuthService.getAndSetCurrentUser()).pipe(
        map(() => true), // Ensure the stream emits a boolean value
        switchMap((proceed) => {
          if (!proceed || !this.shared.user) {
            return this.handleLoginRedirect("User not found after fetch");
          }
          this.user = this.shared.user;

          this.updateAuthCookies();

          const persistentUrl = this.shared.getRedirectUrl();
          const planId = this.shared.getPlanId();
          if (persistentUrl && !planId && persistentUrl.trim() !== "") {
            this.shared.setRedirectUrl(null);
            window.location.href = persistentUrl;
            return of(false);
          }

          return this.loadDataAndNavigate(next);
        }),
        catchError(() => this.handleLoginRedirect("Error fetching user on guard")),
      );
    } else if (this.shared.refreshToken) {
      return from(this.grpcAuthService.refreshToken()).pipe(
        switchMap((response) => {
          if (response.tokens) {
            this.shared.accessToken = response.tokens.accessToken;
            this.shared.refreshToken = response.tokens.refreshToken;
            window.location.reload(); // Reload to re-trigger the guard with the new token
            return of(false); // Prevent navigation until reload
          }
          return this.handleLoginRedirect("Missing tokens in refresh response");
        }),
        catchError(() => this.handleLoginRedirect("Error refreshing token")),
      );
    } else {
      return this.handleLoginRedirect();
    }
  }

  private updateAuthCookies() {
    this.shared.updateCookies();
  }

  private handleOAuthCallback(code: string, state: string): Observable<boolean> {
    this.loadingService.loading$.next(true);
    if (!state) {
      throw new Error("State is missing");
    }
    if (!code) {
      throw new Error("Code is missing");
    }
    return from(this.grpcAuthService.validateCodeOAuth(code, state)).pipe(
      switchMap((response) => {
        const res = response.toObject();
        if (res.tokens?.accessToken && res.tokens?.refreshToken) {
          this.shared.accessToken = res.tokens.accessToken;
          this.shared.refreshToken = res.tokens.refreshToken || "";

          // Clear oauth_state from both storages after successful validation
          sessionStorage.removeItem("oauth_state");
          localStorage.removeItem("oauth_state");

          return from(this.grpcAuthService.getAndSetCurrentUser(res.tokens.accessToken)).pipe(
            switchMap(() => {
              this.updateAuthCookies();

              // If user is missing phone, save pending redirect so modal can use it after phone is collected
              if (this.shared.isUserMissingPhone()) {
                const persistentUrl = this.shared.getRedirectUrl();
                const planId = this.shared.getPlanId();

                let pendingUrl = persistentUrl || environment.SVM_HOME;
                if (planId) {
                  pendingUrl = this.shared.isCurrentPlanFree() ? environment.SVM_HOME : "/checkout/dados-pessoais";
                }
                this.shared.setPendingRedirectUrl(pendingUrl);
                this.shared.setRedirectUrl(null);
                this.shared.setPlanId(null);

                this.loadingService.loading$.next(false);
                this.router.navigate(["/minha-conta"]);
                return of(false);
              }

              const persistentUrl = this.shared.getRedirectUrl();
              const planId = this.shared.getPlanId();
              if (persistentUrl && !planId && persistentUrl.trim() !== "") {
                this.shared.setRedirectUrl(null);
                window.location.href = persistentUrl;
                return of(false);
              } else if (planId) {
                if (this.shared.isCurrentPlanFree()) {
                  this.shared.redirectToOriginAndClearPlan();
                  return of(false);
                }
                this.router.navigate(["/checkout/dados-pessoais"]);
                return of(false);
              }
              return of(true);
            }),
          );
        }
        return this.handleLoginRedirect("OAuth validation failed");
      }),
      catchError((err) => {
        logClientError("OAuth validation error:", err);
        return this.handleLoginRedirect("OAuth validation error");
      }),
    );
  }

  private handleLoginRedirect(errorMessage?: string): Observable<boolean> {
    logClientError("handleLoginRedirect with errors: ", errorMessage);
    this.loadingService.loading$.next(false);
    this.router.navigateByUrl(`/entrar`);
    return of(false);
  }

  private loadDataAndNavigate(next: ActivatedRouteSnapshot): Observable<boolean> {
    this.loadingService.loading$.next(false);

    // Check if there's a planId and we're NOT already going to checkout
    const planId = this.shared.getPlanId();
    const isCheckoutRoute =
      next.routeConfig?.path?.includes("checkout") || next.parent?.routeConfig?.path === "checkout";
    if (planId && !isCheckoutRoute) {
      // Check if the plan is free - if so, skip checkout and redirect to origin
      if (this.shared.isCurrentPlanFree()) {
        this.shared.redirectToOriginAndClearPlan();
        return of(false);
      }
      this.router.navigate(["/checkout/dados-pessoais"]);
      return of(false);
    }

    return of(true);
  }

  private handleCognitoAuth(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const cognitoUser = this.cognitoService.getCurrentUser();
    const redirectUrlCookie = this.cookieService.get("redirectUrl");
    if (
      redirectUrlCookie &&
      redirectUrlCookie !== "null" &&
      redirectUrlCookie !== "undefined" &&
      redirectUrlCookie.trim() !== ""
    ) {
      this.shared.setRedirectUrl(null);
      window.location.href = redirectUrlCookie;
      return of(false);
    }
    if (cognitoUser) {
      return from(
        new Promise<string | boolean>((resolve) => {
          cognitoUser.getSession((err: Error | null, session: null | CognitoUserSession) => {
            if (err) {
              resolve(false);
              return;
            }

            if (session?.isValid()) {
              const processUserAndResolve = (userToProcess: User | null | undefined) => {
                this.user = userToProcess === null ? undefined : userToProcess;
                if (this.user && session) {
                  const accessToken = session.getAccessToken().getJwtToken();
                  const refreshToken = session.getRefreshToken().getToken();
                  this.shared.accessToken = accessToken;
                  this.shared.refreshToken = refreshToken;
                  this.shared.user = this.user;
                  this.shared.updateCookies();

                  // Check if there's a planId and we're NOT already going to checkout
                  const planId = this.shared.getPlanId();
                  const isCheckoutRoute =
                    next.routeConfig?.path?.includes("checkout") || next.parent?.routeConfig?.path === "checkout";

                  if (planId && !isCheckoutRoute) {
                    // Check if the plan is free - if so, skip checkout and redirect to origin
                    if (this.shared.isCurrentPlanFree()) {
                      this.shared.redirectToOriginAndClearPlan();
                      return;
                    }
                    resolve("/checkout/dados-pessoais");
                    return;
                  }

                  const persistentUrl = this.shared.getRedirectUrl();
                  if (persistentUrl && persistentUrl.trim() !== "") {
                    if (persistentUrl.startsWith("http")) {
                      this.shared.setRedirectUrl(null);
                      window.location.href = persistentUrl;
                      return;
                    }
                    resolve(persistentUrl as string);
                  } else {
                    const returnUrlFromQuery = next.queryParams["returnUrl"] || next.queryParams["redirect_uri"];
                    if (returnUrlFromQuery) {
                      resolve(returnUrlFromQuery as string);
                    } else {
                      this.shared.user$.next({ ...this.user });
                      resolve(true);
                    }
                  }
                } else {
                  resolve(false);
                }
              };

              cognitoUser.getUserAttributes((attributesErr: any, attributesData: any) => {
                if (attributesErr) {
                  this.cognitoService
                    .fetchUserAttributes(session.getAccessToken().getJwtToken())
                    .then(processUserAndResolve)
                    .catch(() => resolve(false));
                } else {
                  const userFromCognito: User = this.cognitoService.getUserFromCognito(
                    cognitoUser,
                    session,
                    attributesData,
                  );
                  processUserAndResolve(userFromCognito);
                }
              });
            } else {
              logClientError("Session is not valid");
              resolve(false);
            }
          });
        }),
      ).pipe(
        switchMap((result: string | boolean) => {
          if (typeof result === "string" && result.trim() !== "") {
            this.shared.setRedirectUrl(null);
            window.location.href = result;
            return of(false);
          }
          const isSessionValid = result;
          if (isSessionValid) {
            return this.loadDataAndNavigate(next);
          } else {
            return this.handleLoginRedirect();
          }
        }),
        catchError(() => {
          return this.handleLoginRedirect("Error in auth guard processing");
        }),
      );
    } else {
      const code = next.queryParams["code"];
      if (code) {
        return from(this.cognitoService.exchangeCodeForToken(code)).pipe(
          switchMap(() => {
            this.router.navigateByUrl(`/`);
            return of(false);
          }),
          catchError(() => of(false)),
        );
      } else {
        return this.handleLoginRedirect();
      }
    }
  }
}
